import {
  motion,
} from "framer-motion";

import {
  Link,
  useLocation,
} from "react-router-dom";

import {
  getLanguageFromPath,
  getRoutePath,
} from "../site/config/routes";

import pricingData from "../site/data/pricing";

import Footer from "../template/components/layout/Footer";
import HandwrittenEyebrow from "../template/components/common/HandwrittenEyebrow";
import FaqSection from "../template/components/common/FaqSection";

export default function PricingPage() {
  const location =
    useLocation();

  const language =
    getLanguageFromPath(
      location.pathname
    );

  const localized =
    (value) => {
      if (
        value === null ||
        value === undefined
      ) {
        return "";
      }

      if (
        typeof value ===
        "string"
      ) {
        return value;
      }

      return (
        value[language] ??
        value.fi ??
        value.en ??
        ""
      );
    };

  const hero =
    pricingData.hero;

  return (
    <>
      <section
        className="
          relative
          overflow-hidden
          border-b
          border-border
          bg-canvas
        "
      >
        <motion.div
          className="
            pointer-events-none
            absolute
            inset-y-0
            right-0
            z-0
            w-[54%]
            overflow-hidden

            max-sm:right-[-35%]
            max-sm:w-full
          "
          initial={{
            opacity: 0,
            scale: 1.035,
          }}
          animate={{
            opacity: 1,
            scale: 1,
          }}
          transition={{
            duration: 1.4,
            delay: 0.1,
            ease: [
              0.22,
              1,
              0.36,
              1,
            ],
          }}
        >
          <img
            src={hero.image}
            alt=""
            className="
              h-full
              w-full
              object-cover
              opacity-30

              sm:opacity-45
              lg:opacity-75
            "
            style={{
              objectPosition:
                hero.imagePosition,
            }}
          />

          <div
            className="
              absolute
              inset-0
              bg-gradient-to-r
              from-canvas
              via-canvas/45
              to-transparent
            "
          />
        </motion.div>

        <div
          className="
            relative
            z-10
            mx-auto
            flex
            min-h-[74svh]
            w-full
            max-w-editorial
            items-end
            px-6
            pb-20
            pt-36

            sm:px-10
            sm:pb-28
            sm:pt-44

            lg:px-16
            lg:pb-32
          "
        >
          <motion.div
            className="
              min-w-0
              max-w-5xl
            "
            initial={{
              opacity: 0,
              y: 28,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 0.9,
              ease: [
                0.22,
                1,
                0.36,
                1,
              ],
            }}
          >
            <HandwrittenEyebrow
              label={localized(
                hero.eyebrow
              )}
            />

            <h1
              className="
                m-0
                mt-8
                max-w-5xl
                font-display
                text-[clamp(4rem,9vw,9rem)]
                font-normal
                leading-[0.84]
                tracking-[-0.055em]
                text-text
              "
            >
              {localized(
                hero.title
              )}
            </h1>

            <p
              className="
                mb-0
                mt-9
                max-w-2xl
                text-sm
                leading-7
                text-muted

                sm:text-base
                sm:leading-8
              "
            >
              {localized(
                hero.description
              )}
            </p>
          </motion.div>
        </div>
      </section>

      <section
        className="
          overflow-x-hidden
          bg-canvas
        "
      >
        <div
          className="
            mx-auto
            w-full
            max-w-editorial
            px-6
            py-20

            sm:px-10
            sm:py-28

            lg:px-16
            lg:py-36
          "
        >
          <div
            className="
              mb-16
              flex
              flex-col
              gap-6
              border-b
              border-border
              pb-7

              sm:flex-row
              sm:items-end
              sm:justify-between

              lg:mb-24
            "
          >
            <p
              className="
                m-0
                max-w-2xl
                text-[10px]
                uppercase
                tracking-[0.2em]
                text-muted
              "
            >
              {localized(
                pricingData.notice
              )}
            </p>

            <span
              className="
                font-display
                text-3xl
                italic
                text-muted
              "
              aria-hidden="true"
            >
              01—03
            </span>
          </div>

          <div
            className="
              space-y-24

              lg:space-y-36
            "
          >
            {pricingData.groups.map(
              (group) => (
                <motion.article
                  key={group.id}
                  className="
                    min-w-0
                  "
                  initial={{
                    opacity: 0,
                    y: 24,
                  }}
                  whileInView={{
                    opacity: 1,
                    y: 0,
                  }}
                  viewport={{
                    once: true,
                    amount: 0.12,
                  }}
                  transition={{
                    duration: 0.8,
                    ease: [
                      0.22,
                      1,
                      0.36,
                      1,
                    ],
                  }}
                >
                  <div
                    className="
                      mb-10
                      grid
                      min-w-0
                      gap-8

                      lg:grid-cols-[minmax(0,0.8fr)_minmax(320px,0.45fr)]
                      lg:items-end
                    "
                  >
                    <div
                      className="
                        min-w-0
                      "
                    >
                      <p
                        className="
                          mb-5
                          text-[9px]
                          font-medium
                          uppercase
                          tracking-[0.28em]
                          text-muted
                        "
                      >
                        {group.number} /{" "}
                        {localized(
                          group.title
                        )}
                      </p>

                      <h2
                        className="
                          m-0
                          min-w-0
                          max-w-full
                          font-display
                          text-[2.45rem]
                          font-normal
                          leading-[0.92]
                          tracking-[-0.04em]
                          text-text
                          [hyphens:none]
                          [overflow-wrap:normal]
                          [word-break:normal]

                          sm:text-[clamp(3.4rem,6vw,6.8rem)]
                          sm:leading-[0.88]
                          sm:tracking-[-0.05em]
                        "
                      >
                        {localized(
                          group.title
                        )}
                      </h2>
                    </div>

                    <p
                      className="
                        m-0
                        min-w-0
                        max-w-md
                        text-sm
                        leading-7
                        text-muted

                        sm:text-base
                        sm:leading-8
                      "
                    >
                      {localized(
                        group.description
                      )}
                    </p>
                  </div>

                  <div
                    className="
                      grid
                      min-w-0
                      border-l
                      border-t
                      border-border

                      md:grid-cols-2
                    "
                  >
                    {group.packages.map(
                      (item) => (
                        <div
                          key={localized(
                            item.name
                          )}
                          className={`
                            relative
                            flex
                            min-h-[390px]
                            min-w-0
                            flex-col
                            border-b
                            border-r
                            border-border
                            p-7

                            sm:p-8

                            ${
                              item.featured
                                ? "bg-surface"
                                : "bg-canvas"
                            }
                          `}
                        >
                          {item.featured && (
                            <span
                              className="
                                mb-7
                                w-fit
                                rounded-full
                                border
                                border-border
                                px-3
                                py-1.5
                                text-[8px]
                                font-semibold
                                uppercase
                                tracking-[0.2em]
                                text-muted
                              "
                            >
                              {language ===
                              "en"
                                ? "Popular"
                                : "Suosittu"}
                            </span>
                          )}

                          <div
                            className="
                              min-w-0
                            "
                          >
                            <h3
                              className="
                                m-0
                                max-w-full
                                font-display
                                text-4xl
                                font-normal
                                leading-none
                                tracking-[-0.035em]
                                [overflow-wrap:break-word]
                              "
                            >
                              {localized(
                                item.name
                              )}
                            </h3>

                            <p
                              className="
                                mb-0
                                mt-3
                                text-[9px]
                                uppercase
                                tracking-[0.2em]
                                text-muted
                              "
                            >
                              {localized(
                                item.meta
                              )}
                            </p>
                          </div>

                          <p
                            className="
                              mb-0
                              mt-8
                              font-display
                              text-5xl
                              leading-none
                              tracking-[-0.045em]
                            "
                          >
                            {item.price}
                          </p>

                          <ul
                            className="
                              mb-0
                              mt-auto
                              space-y-3
                              pt-10
                            "
                          >
                            {(
                              item.features[
                                language
                              ] ||
                              item.features
                                .fi ||
                              []
                            ).map(
                              (
                                feature
                              ) => (
                                <li
                                  key={
                                    feature
                                  }
                                  className="
                                    flex
                                    min-w-0
                                    gap-3
                                    text-xs
                                    leading-6
                                    text-muted
                                  "
                                >
                                  <span
                                    className="
                                      mt-[11px]
                                      h-px
                                      w-4
                                      shrink-0
                                      bg-border
                                    "
                                    aria-hidden="true"
                                  />

                                  <span
                                    className="
                                      min-w-0
                                    "
                                  >
                                    {
                                      feature
                                    }
                                  </span>
                                </li>
                              )
                            )}
                          </ul>
                        </div>
                      )
                    )}
                  </div>
                </motion.article>
              )
            )}
          </div>
        </div>
      </section>

      <section
        className="
          overflow-x-hidden
          border-y
          border-border
          bg-surface
        "
      >
        <div
          className="
            mx-auto
            grid
            w-full
            max-w-editorial
            gap-12
            px-6
            py-20

            sm:px-10
            sm:py-28

            lg:grid-cols-[minmax(0,0.8fr)_minmax(320px,0.55fr)]
            lg:items-end
            lg:px-16
            lg:py-32
          "
        >
          <div
            className="
              min-w-0
            "
          >
            <p
              className="
                mb-5
                text-[9px]
                uppercase
                tracking-[0.28em]
                text-muted
              "
            >
              {localized(
                pricingData.extras
                  .eyebrow
              )}
            </p>

            <h2
              className="
                m-0
                max-w-4xl
                font-display
                text-[clamp(2.75rem,12vw,3rem)]
                font-normal
                leading-[0.92]
                tracking-[-0.045em]

                sm:text-[clamp(3rem,6vw,6rem)]
                sm:leading-[0.9]
              "
            >
              {localized(
                pricingData.extras
                  .title
              )}
            </h2>
          </div>

          <div
            className="
              min-w-0
              border-t
              border-border
            "
          >
            {pricingData.extras.items.map(
              (item) => (
                <div
                  key={localized(
                    item.label
                  )}
                  className="
                    flex
                    min-w-0
                    items-center
                    justify-between
                    gap-5
                    border-b
                    border-border
                    py-5

                    sm:gap-8
                  "
                >
                  <span
                    className="
                      min-w-0
                      text-xs
                      uppercase
                      tracking-[0.16em]
                      text-muted
                    "
                  >
                    {localized(
                      item.label
                    )}
                  </span>

                  <span
                    className="
                      shrink-0
                      font-display
                      text-2xl
                    "
                  >
                    {localized(
                      item.price
                    )}
                  </span>
                </div>
              )
            )}
          </div>
        </div>
      </section>

      {/* ======================================================
          FAQ
          ====================================================== */}

      <FaqSection
        language={language}
        sectionKey="pricing"
      />

      <section
        className="
          overflow-x-hidden
          bg-text
          text-canvas
        "
      >
        <div
          className="
            mx-auto
            grid
            w-full
            max-w-editorial
            gap-12
            px-6
            py-24

            sm:px-10
            sm:py-32

            lg:grid-cols-[1fr_auto]
            lg:items-end
            lg:px-16
            lg:py-40
          "
        >
          <div
            className="
              min-w-0
            "
          >
            <p
              className="
                mb-6
                text-[9px]
                uppercase
                tracking-[0.28em]
                text-canvas/60
              "
            >
              {language === "en"
                ? "Your session"
                : "Sinun kuvauksesi"}
            </p>

            <h2
              className="
                m-0
                max-w-4xl
                font-display
                text-[clamp(3rem,13vw,3.5rem)]
                font-normal
                leading-[0.9]
                tracking-[-0.05em]

                sm:text-[clamp(3.5rem,7vw,7rem)]
                sm:leading-[0.88]
              "
            >
              {language === "en"
                ? "Tell me what you have in mind."
                : "Kerro, mitä sinulla on mielessä."}
            </h2>
          </div>

          <Link
            to={getRoutePath(
              "contact",
              language
            )}
            className="
              group
              inline-flex
              min-h-12
              items-center
              gap-4
              border-b
              border-canvas
              pb-2
              text-[10px]
              font-semibold
              uppercase
              tracking-[0.2em]
              text-canvas
            "
          >
            {language === "en"
              ? "Get in touch"
              : "Ota yhteyttä"}

            <span
              className="
                transition-transform
                duration-300
                group-hover:translate-x-1
              "
              aria-hidden="true"
            >
              →
            </span>
          </Link>
        </div>
      </section>

      <Footer
        language={language}
      />
    </>
  );
}